import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import { autoinject, bind, componentReaction, deepStringFind, Enums, inject } from 'Mri.Web.Common';
import * as React from 'react';
import { AccountStore } from '../Account/AccountStore';
import { AppStore } from '../Core/AppStore';
import { DisplayListViewModel } from '../DisplayFilters/DisplayListViewModel';
import { cellIdToString } from './DisplayCells';

export interface IDisplayCellWrapperProps {
    readonly vm: DisplayListViewModel;
    readonly cellId: string | Mri.WebApi.MeasurementId;
    readonly className?: string;
    readonly style?: React.CSSProperties;
    readonly dropdownOpen?: boolean;
}

@autoinject
@observer
export class DisplayCellWrapper extends React.Component<IDisplayCellWrapperProps> {
    public render() {
        const { className, style, cellId } = this.props;
        const cellIdString = cellIdToString(cellId);

        if (this.isSticky) {
            return (
                <DisplayStickyCellWrapper
                    cellId={cellIdString}
                    className={className + this.className}
                    vm={this.props.vm}
                    dropdownOpen={this.props.dropdownOpen}
                >
                    {this.props.children ?? '-'}
                </DisplayStickyCellWrapper>
            );
        } else {
            return (
                <td id={cellIdString} className={className + ' display-cell ' + this.className} style={style}>
                    {this.props.children ?? '-'}
                </td>
            );
        }
    }

    @computed
    private get isSorted() {
        const { cellId } = this.props;
        return this.appStore.storage.sortedColumn == cellIdToString(cellId);
    }

    @computed
    private get isSticky() {
        const { cellId, vm } = this.props;
        const stickyColumn = cellIdToString(cellId);

        if (!vm.showStickyColumns) return false;

        return this.props.vm.stickyColumns.includes(stickyColumn);
    }

    @computed
    private get className() {
        const { cellId } = this.props;
        const { searchedColumn } = this.props.vm;
        const cellString = cellIdToString(cellId);
        let className = '';

        if (searchedColumn.length > 0 && deepStringFind(cellString, searchedColumn)) {
            className = className + ' highlighted-cell';
        }
        if (!this.accountStore.storage.wrappedHeaders) {
            className = className + ' no-wrap';
        }
        if (this.isSorted) {
            className = className + ' sorted-cell';
        }
        return className;
    }

    @inject
    private accountStore!: AccountStore;
    @inject
    private appStore!: AppStore;
}

export interface IDisplayStickyCellWrapperProps {
    readonly vm: DisplayListViewModel;
    readonly cellId: string;
    readonly className?: string;
    readonly style?: React.CSSProperties;
    readonly dropdownOpen?: boolean;
}

@autoinject
@observer
export class DisplayStickyCellWrapper extends React.Component<IDisplayStickyCellWrapperProps> {
    public render() {
        const { className, cellId } = this.props;

        return (
            <th
                id={cellId}
                className={
                    className +
                    ' sticky-column text-center ' +
                    this.dropdownClass +
                    this.highlight +
                    this.sortedHighlight
                }
                ref={this.bindHeaderRef}
                style={this.stickyStyle}
            >
                {this.props.children}
            </th>
        );
    }

    @bind
    @action
    private bindHeaderRef(ref: HTMLTableHeaderCellElement | null) {
        this.headerRef = ref;
    }

    @computed
    private get dropdownClass() {
        if (this.props.vm.stickyColumnDropdownOpen && !this.props.dropdownOpen) {
            return ' non-dropdown ';
        }
    }

    @computed
    private get highlight() {
        const { cellId } = this.props;
        const { searchedColumn } = this.props.vm;
        const cellString = cellIdToString(cellId);

        if (searchedColumn.length > 0 && deepStringFind(cellString, searchedColumn)) {
            return ' highlighted-cell ';
        }
        return '';
    }

    @computed
    private get sortedHighlight() {
        const { cellId } = this.props;
        if (this.appStore.storage.sortedColumn == cellIdToString(cellId)) {
            return ' sorted-cell ';
        }
    }

    @computed
    private get stickyStyle() {
        if (this.props.vm.draggedHeaderId == cellIdToString(this.props.cellId)) {
            return {
                minWidth: this.props.vm.draggedHeaderWidth,
                left: this.vmStickyOffset?.offset,
            };
        } else {
            return { left: this.vmStickyOffset?.offset };
        }
    }

    @computed
    private get vmStickyOffset() {
        return this.props.vm.stickyOffsets.find(c => c.id == this.props.cellId);
    }

    @inject
    private appStore!: AppStore;
    @observable
    private updatingOffset: boolean = false;
    @observable
    private stickyOffset?: number = 0;
    @observable
    private timer?: number;
    @observable
    private headerRef: HTMLTableHeaderCellElement | null = null;
}
